"use client";

import * as React from "react";
import generateRandomId from "@/helpers/generateRandomId";

import Toast from "@/components/_ui/Toast/Toast";

export type ToastModel = Omit<React.ComponentProps<typeof Toast>, "onCancel">;
export type ToastStatus = ToastModel["status"];

const TOAST_LIMIT = 10; // Adjust as needed
const TOAST_REMOVE_DELAY = 5000; // 5 seconds, adjust as needed

interface ActionType {
    ADD_TOAST: "ADD_TOAST";
    REMOVE_TOAST: "REMOVE_TOAST";
}

type Action =
    | {
          type: ActionType["ADD_TOAST"];
          toast: ToastModel;
      }
    | {
          type: ActionType["REMOVE_TOAST"];
          toastId: string;
      };

interface State {
    toasts: ToastModel[];
}

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "ADD_TOAST":
            return {
                ...state,
                toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
            };
        case "REMOVE_TOAST":
            return {
                ...state,
                toasts: state.toasts.filter((t) => t.id !== action.toastId),
            };
    }
};

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((listener) => {
        listener(memoryState);
    });
}

function useToast() {
    const [state, setState] = React.useState<State>(memoryState);

    React.useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        };
    }, [state]);

    const addToast = React.useCallback((title: string, status: ToastStatus, href?: string) => {
        const id = generateRandomId();

        dispatch({
            type: "ADD_TOAST",
            toast: {
                id,
                title,
                status,
                href,
            },
        });

        // Auto-remove toast after delay
        setTimeout(() => {
            dispatch({ type: "REMOVE_TOAST", toastId: id });
        }, TOAST_REMOVE_DELAY);

        return id;
    }, []);

    const removeToast = (id: string) => {
        dispatch({ type: "REMOVE_TOAST", toastId: id });
    };

    return {
        toasts: state.toasts,
        addToast,
        removeToast,
    };
}

export { useToast };
